import React, { memo, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import { Route, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Box, Skeleton } from 'transfix-ui/core'
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { BidAcceptedModal, BidCounteredModal } from 'apps/fleet-planner/components/bids'
import { BidSubmitModal } from 'apps/fleet-planner/components/bids/BidSubmitModal'
import { ShipmentMap } from 'apps/fleet-planner/components/eld-tracking/ShipmentMap'
import { useLoadDetailsModalStyles } from 'apps/fleet-planner/components/load-details/LoadDetailsModal'
import { useUserContext } from 'apps/fleet-planner/context'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import {
  useBidSubmit,
  useFMSAnalytics,
  useMembershipValidation,
  useShipmentOffersApi,
  useTrackConversion,
} from 'apps/fleet-planner/hooks'
import {
  BiddingPermissionsModal,
  TActionType,
} from 'apps/fleet-planner/pages/BiddingPermissionsModal'
import { CarrierComplianceModal } from 'apps/fleet-planner/pages/CarrierComplianceModal'
import {
  ITestCarrierProdBookingModal,
  TestCarrierProdBookingModal,
  isTestCarrier,
} from 'apps/fleet-planner/pages/TestCarrierProdBookingModal'
import { BookBidSection } from 'apps/fleet-planner/pages/marketplace/BookBidSection'
import { LoadUnavailable } from 'apps/fleet-planner/pages/marketplace/LoadUnavailable'
import { BidModal, TBidAction } from 'apps/fleet-planner/pages/marketplace/load-details/BidModal'
import { MarketplaceLoadDetailsSection } from 'apps/fleet-planner/pages/marketplace/load-details/MarketplaceLoadDetailsSection'
import { MarketplaceStopSummary } from 'apps/fleet-planner/pages/marketplace/stops/MarketplaceStopSummary'
import { MarketplaceStops } from 'apps/fleet-planner/pages/marketplace/stops/MarketplaceStops'
import { TEAM_MEMBER_USER_TYPES, usePostWaterfallReoffer } from 'apps/fleet-planner/types'
import { fetchDispatchers } from 'apps/fleet-planner/utils/actions/dispatchers'
import { fetchDrivers } from 'apps/fleet-planner/utils/actions/drivers'
import { isCarrierAllowedToBookShipment } from 'apps/fleet-planner/utils/isCarrierAllowedToBookShipment'
import { isOfferADedicatedRateOfferedToMoreCarriers } from 'apps/fleet-planner/utils/offerHelpers'
import { MODAL_TYPES, ModalBase, Toast, useToastState } from 'transfix-ui'
import { DISPLAY } from 'constants/display'

export interface IPermissionModal {
  isOpen: boolean
  actionType?: TActionType
  userType?: TEAM_MEMBER_USER_TYPES
}

interface IMarketplaceLoadDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  onContactClick: () => void
  onDecline?: (shipmentId: number) => void
  route?: string
  /**
   * @default false
   */
  showMap?: boolean
}

export const MarketplaceLoadDetailsModal = memo<IMarketplaceLoadDetailsModalProps>(
  ({ isOpen, onClose, onContactClick, onDecline, route = 'book', showMap = false }) => {
    const { userMotorCarriers = [] } = useMembershipContext()
    const { trackEvent } = useFMSAnalytics()
    const { userData } = useUserContext()
    const { motorCarriers = [] } = userData || {}
    const history = useHistory()

    const { shipmentId } = useParams<{ shipmentId: string }>()
    const dispatch = useDispatch()
    const [{ eventSource, isShipmentOffer }] = useQueryParams({
      eventSource: StringParam,
      isShipmentOffer: BooleanParam,
    })
    const { getFlag } = useFeatureFlagContext()
    const { enabled: fmsCounterBiddingEnabled } = getFlag('fmsCounterBidding')
    const { enabled: isfmsDeclineBidEnabled } = getFlag('fmsDeclineBid')
    const { enabled: isHighRiskFlagEnabled } = getFlag('highRiskLoadsFlag')
    const { enabled: isPreBookingMapEnabled } = getFlag('fmsPrebookingLoadDetailsMap')

    const {
      bidUpdateSuccessHandler,
      clearUpdatedBid,
      isAccepted,
      isCountered,
      isPending,
      updatedBid,
    } = useBidSubmit()

    const { pathname = '', search } = useLocation()

    const {
      findAllShipmentOffer: findShipmentOffer,
      getAllShipmentOffers: getShipmentOffers,
      isLoading: isShipmentOffersLoading,
    } = useShipmentOffersApi()

    const {
      getMarketplaceLoad,
      getMarketplaceShipmentCarrierCompliance,
      isLoadUnavailable,
      isLoading,
      resetShipmentDetails,
      shipmentDetails,
    } = useMarketplaceContext()

    const { id: shipmentDetailsId, info, stops = [], opsRep } = shipmentDetails || {}
    const { isHighRisk } = info || {}

    const shipmentOffer = findShipmentOffer(shipmentDetailsId)
    const { carrierTotalRate, waterfallTimeLeftMinutes = 0 } = shipmentOffer || {}
    const { getUserTypeForCarrier, userCanBidAndBook } = useMembershipValidation()
    const { isDatShipmentId, isTruckstopShipmentId, setTrackConversion } = useTrackConversion()
    const { error, mutate: postWaterfallReOffer } = usePostWaterfallReoffer({
      id: shipmentDetailsId,
    })

    // Enables the map only on PrebookingLoadDetails
    const isMapVisible = isPreBookingMapEnabled && showMap && shipmentDetailsId
    // Hides nearby location names on map
    const hideNearbyAttractions = isHighRiskFlagEnabled && isHighRisk

    const navigateToRateConPage = (motorCarrierId?: number) => {
      if (motorCarrierId) {
        history.push(`/book/${shipmentDetailsId}/rate-confirmation?carrierId=${motorCarrierId}`)
      } else {
        history.push(`/book/${shipmentDetailsId}/rate-confirmation`)
      }
    }

    const reOfferLoadAndNavigateToRateConPage = async (motorCarrierId?: number) => {
      try {
        await postWaterfallReOffer()
        navigateToRateConPage(motorCarrierId)
      } catch (e) {
        // no-op
        // error is captured in usePostWaterfallReoffer
      }
    }

    const openOpsRepModalHandler = () => {
      trackEvent('Book Load', 'CLICKED_CONTACT_BROKER_CTA')
      if (shipmentDetails?.id) {
        onContactClick()
      }
    }

    // Fetch load info based on shipmentId provided in the url
    useEffect(() => {
      if (shipmentId) {
        getMarketplaceLoad(shipmentId)
      }
    }, [getMarketplaceLoad, shipmentId])

    useEffectOnce(() => {
      if (!isShipmentOffersLoading && isShipmentOffer) {
        getShipmentOffers()
      }
    })

    const [isComplianceModalOpen, setIsComplianceModalOpen] = useState<boolean>(false)

    const [testCarrierProdBookingModal, setTestCarrierProdBookingModal] = useState<
      ITestCarrierProdBookingModal
    >({
      isOpen: false,
      onSubmit: () => {},
    })
    const [permissionsModal, setPermissionsModal] = useState<IPermissionModal>({
      isOpen: false,
      actionType: undefined,
      userType: undefined,
    })

    const closeHandler = () => {
      if (isLoadUnavailable) trackEvent('Marketplace Load', 'VIEWED_LOAD_NOT_AVAILABLE')
      resetShipmentDetails()
      onClose()
    }

    const closeBidModal = () => {
      clearUpdatedBid()
      getMarketplaceLoad(shipmentId)
    }

    const resetPermissionsModal = () => {
      setPermissionsModal({
        isOpen: false,
        actionType: undefined,
        userType: undefined,
      })
    }

    const resetTestCarrierProdBookingModal = () => {
      setTestCarrierProdBookingModal({
        isOpen: false,
        onSubmit: () => {},
      })
    }

    useEffect(() => {
      if (pathname.includes('/compliance')) {
        setIsComplianceModalOpen(true)
      }
    }, [pathname])

    const openBookBidModalHandler = () => {
      /**
       * If user is not a dispatcher, prevent them from proceeding
       */
      if (isDatShipmentId(shipmentId)) {
        setTrackConversion('web', 'dat_posting')
      }
      if (isTruckstopShipmentId(shipmentId)) {
        setTrackConversion('web', 'truckstop_posting')
      }
      if (motorCarriers.length === 1) {
        const [singleCarrier] = motorCarriers || []
        const userType = getUserTypeForCarrier(singleCarrier.id)
        trackEvent('Book Load', 'CLICKED_BID_ON_BOOKABLE_LOAD')
        // If the user is not able to do this, show them a modal
        if (!userCanBidAndBook(singleCarrier.id)) {
          trackEvent('Book Load', 'DOES_NOT_HAVE_PERMISSION_TO_BID_MODAL_OPENED', {
            shipmentId,
            userMotorCarriers,
            motorCarrierId: singleCarrier.id,
            userType,
          })
          setPermissionsModal({
            isOpen: true,
            userType,
            actionType: 'bid',
          })
          return
        }
      }

      history.push(`/${route}/${shipmentId}/bid${search}`)
    }

    // This safe version checks if the current env is production and the user belongs to a test carrier
    // If this is true, we show a warning dialog before proceeding
    const openBookBidModalHandlerSafe = () => {
      if (isTestCarrier(motorCarriers)) {
        setTestCarrierProdBookingModal({
          isOpen: true,
          onSubmit: openBookBidModalHandler,
        })
      } else {
        openBookBidModalHandler()
      }
    }

    const closeBookBidModalHandler = () => {
      history.push(`/${route}/${shipmentId}${search}`)
    }

    const openComplianceModal = () => setIsComplianceModalOpen(true)
    const closeComplianceModal = () => setIsComplianceModalOpen(false)

    const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
    const bidSuccessHandler = (action: TBidAction, bidId?: number) => {
      bidUpdateSuccessHandler(shipmentId, bidId)
      if (action === 'delete') {
        openToast({
          toastType: 'default',
          toastMessage: 'Bid Deleted',
        })
      }
    }

    const bidErrorHandler = (action: TBidAction) =>
      openToast({
        toastType: 'alert',
        toastMessage: action === 'submit' ? 'Error Submitting Bid' : 'Error Deleting Bid',
      })

    /**
     * This method brings the modal into focus so we can tab and scroll through the content
     * using keyboard buttons.
     */
    const modalRef = useRef<HTMLInputElement>(null)
    useEffect(() => {
      if (modalRef.current) modalRef.current.focus()
    }, [])

    const declineOfferHandler = () => {
      if (onDecline && shipmentDetailsId) {
        onDecline(shipmentDetailsId)
      }
    }

    const declineOfferHandlerNew = (response: string = '') => {
      // declining an offer will be handled within the component following the release of DL O4
      // this function only displays a toast on success and refreshes the offers list
      if (response === 'success') {
        // in the case where the decline is successful, we close the modal and that hides toast messages
        // therefore toast is only being displayed on failure
        if (onDecline) {
          onDecline(shipmentDetailsId)
        }
        onClose()
      } else {
        openToast({
          toastType: 'alert',
          toastMessage: <span>Offer decline unsuccessful. Please try again.</span>,
        })
      }
    }

    const bookNowHandler = async () => {
      const motorCarrierId = updatedBid?.carrier?.motorCarrierId

      clearUpdatedBid()
      if (isDatShipmentId(shipmentId)) {
        setTrackConversion('web', 'dat_posting')
      }
      // if user only has a single MC, run a compliance check automatically
      // open the compliance modal if they fail this check
      if (motorCarriers.length === 1) {
        const [singleCarrier] = motorCarriers || []
        const userType = getUserTypeForCarrier(singleCarrier.id)

        /**
         * If user is not a dispatcher, prevent them from proceeding
         */
        if (!userCanBidAndBook(singleCarrier.id)) {
          trackEvent('Book Load', 'DOES_NOT_HAVE_PERMISSION_TO_BOOK_MODAL_OPENED', {
            shipmentId,
            userMotorCarriers,
            motorCarrierId: singleCarrier.id,
            userType,
          })
          setPermissionsModal({
            isOpen: true,
            userType,
            actionType: 'book',
          })
          return
        }

        const carrierCompliance = await getMarketplaceShipmentCarrierCompliance(
          shipmentDetailsId,
          singleCarrier.dotNumber,
          singleCarrier.mcNumber
        )

        if (!isCarrierAllowedToBookShipment(carrierCompliance, shipmentDetails)) {
          openComplianceModal()
        } else if (isOfferADedicatedRateOfferedToMoreCarriers(shipmentOffer)) {
          reOfferLoadAndNavigateToRateConPage()
        } else {
          navigateToRateConPage()
        }
      } else if (isOfferADedicatedRateOfferedToMoreCarriers(shipmentOffer)) {
        reOfferLoadAndNavigateToRateConPage(motorCarrierId)
      } else {
        navigateToRateConPage(motorCarrierId)
      }
    }

    // This safe version checks if the current env is production and the user belongs to a test carrier
    // If this is true, we show a warning dialog before proceeding
    const bookNowHandlerSafe = () => {
      if (isTestCarrier(motorCarriers)) {
        setTestCarrierProdBookingModal({
          isOpen: true,
          onSubmit: bookNowHandler,
        })
      } else {
        bookNowHandler()
      }
    }

    const classes = useLoadDetailsModalStyles()

    const titleComponent = (
      <div data-test={`modal-title-id-${shipmentId}`} className='mr2'>
        {isLoading ? (
          <Skeleton variant='rectangular' width={180} height={26} />
        ) : (
          `Load #TFX-${shipmentId || DISPLAY.empty}`
        )}
      </div>
    )

    /**
     * Fetch all drivers from the store for Load details modal
     */
    useEffectOnce(() => {
      dispatch(fetchDrivers())
      dispatch(fetchDispatchers())
    })

    // Track analytics eventss for successfully viewing an available load.
    useEffect(() => {
      if (isOpen) {
        trackEvent('Book Load', 'VIEWED_BOOKABLE_LOAD_MODAL')
      }
      // don't want to include trackEvent here, will result in reduntant events
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Track analytics events for viewing an unavailable load.
    useEffect(() => {
      if (!isLoading && isLoadUnavailable) {
        trackEvent('Book Load', 'VIEWED_UNAVAILABLE_LOAD')
      }
      // don't want to include trackEvent here, will result in reduntant events
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadUnavailable, isLoading])

    // Clear UTM params
    useEffect(() => {
      if (eventSource === 'savedSearchEmail' && (isLoadUnavailable || shipmentDetailsId)) {
        history.push(`/${route}/${shipmentId}`)
      }
    }, [eventSource, history, isLoadUnavailable, route, shipmentDetailsId, shipmentId])

    return (
      <ModalBase
        bodyClassName='justify-center'
        title={titleComponent}
        size={10}
        modalType={MODAL_TYPES.form}
        open={isOpen}
        onClose={closeHandler}
        dataTest='modal-load-details'
        className='fp-loads-modal fp-load-details-modal'
        scrollableHeight
        isClose>
        {isLoadUnavailable ? (
          <LoadUnavailable />
        ) : (
          <>
            <Box className={classes.content}>
              <div className={classes.contentInner} ref={modalRef}>
                {error && (
                  <div className='pt3'>
                    <InlineMessage
                      type={INLINE_MESSAGE_VARIANTS.ALERT}
                      message='Sorry, something went wrong and we were unable to offer you this load.'
                    />
                  </div>
                )}
                <MarketplaceStopSummary className='flex items-center' stops={stops} />

                {isMapVisible && (
                  <ShipmentMap
                    isTrackableStatus={false}
                    hideNearbyAttractions={hideNearbyAttractions}
                  />
                )}

                <MarketplaceLoadDetailsSection shipmentDetails={shipmentDetails} />
                <MarketplaceStops stops={stops} />
              </div>
            </Box>
            <Box className={classes.sidebar}>
              <Box className={classes.sidebarInner}>
                {isShipmentOffersLoading ? (
                  <Box alignContent='center'>
                    <Box paddingY={2}>
                      <Skeleton variant='rectangular' height='20px' width='14%' component='div' />
                      <Box pt={2}>
                        <Skeleton variant='rectangular' height='14px' width='36%' component='div' />
                      </Box>
                      <Box pt={2}>
                        <Skeleton
                          variant='rectangular'
                          height='35px'
                          width='100%'
                          component='div'
                        />
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <BookBidSection
                    route={route}
                    onClickContactUs={openOpsRepModalHandler}
                    onClickBid={openBookBidModalHandlerSafe}
                    onClickBookNow={bookNowHandlerSafe}
                    onDeclineHandler={
                      isfmsDeclineBidEnabled ? declineOfferHandlerNew : declineOfferHandler
                    }
                    waterfallTimeLeftMinutes={waterfallTimeLeftMinutes}
                    waterfallOfferRate={carrierTotalRate}
                    isBookNowDisabled={!!error}
                    shipmentOffer={shipmentOffer}
                  />
                )}
              </Box>
            </Box>
            {!isLoading && (
              <Route exact path={[`/${route}/:shipmentId/bid`, `/${route}/:shipmentId/bid/:bidId`]}>
                <BidModal
                  route={route}
                  onClose={closeBookBidModalHandler}
                  isOpen
                  onSuccess={bidSuccessHandler}
                  onError={bidErrorHandler}
                />
              </Route>
            )}
            <Toast
              variation={toastType}
              open={toastOpen}
              onClose={closeToast}
              message={toastMessage}
            />
            <TestCarrierProdBookingModal
              onClose={resetTestCarrierProdBookingModal}
              isOpen={testCarrierProdBookingModal.isOpen}
              onSubmit={testCarrierProdBookingModal.onSubmit}
            />
            <CarrierComplianceModal
              onClose={closeComplianceModal}
              isOpen={isComplianceModalOpen}
              onSubmit={openBookBidModalHandler}
            />
            <BiddingPermissionsModal
              onClose={resetPermissionsModal}
              isOpen={permissionsModal.isOpen}
              actionType={permissionsModal.actionType}
              userType={permissionsModal.userType}
              opsRep={opsRep}
            />
            <BidSubmitModal isOpen={isOpen && isPending} onClose={closeBidModal} />
            <BidAcceptedModal
              isOpen={isOpen && isAccepted}
              updatedBid={updatedBid}
              onClose={closeBidModal}
              onBookNow={bookNowHandler}
            />
            {fmsCounterBiddingEnabled && (
              <BidCounteredModal
                isOpen={isOpen && isCountered}
                updatedBid={updatedBid}
                onClose={closeBidModal}
                onBookNow={bookNowHandler}
              />
            )}
          </>
        )}
      </ModalBase>
    )
  }
)
